import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { Theme, useTheme } from 'bold-ui'
import { PecFormApi } from 'components/form'
import { UseFieldProps, usePecField } from 'components/form/core/hooks/useField'
import { resetAllFieldStates } from 'components/form/core/util'
import { ComponentProps, PropsWithChildren, useCallback } from 'react'
import { emptyArrayOf } from 'util/array'
import { identity } from 'util/functions'
import { v4 as uuidv4 } from 'uuid'

import { getListWithRestoredItem } from './util/util-editableList'

export interface RowType {
  _id: ID
}

interface EditableFormListOptions<T extends RowType> extends UseFieldProps<T[]> {
  isSameRow?: (itemA: T, itemB: T) => boolean
}

const defaultIsSameRow = <T extends RowType>(itemA: T, itemB: T) => itemA._id === itemB._id

export function useEditableListField<T extends RowType>(options: EditableFormListOptions<T>) {
  const { isSameRow = defaultIsSameRow, format = identity, ...rest } = options

  const fieldProps = usePecField({ ...rest, format })
  const { input, meta } = fieldProps

  const handleSubmit = (formValues: T, formApi: PecFormApi) =>
    new Promise((resolve) => {
      const value = { ...formValues, _id: uuidv4() }

      input.onChange([...(input.value ?? []), value])
      input.onBlur() // usado pra marcar o field como touched no form externo
      resetAllFieldStates(formApi)

      resolve(true)
    }).then(() => setTimeout(formApi.reset))

  const handleSubmitWithoutReset = (formValues: T) =>
    new Promise((resolve) => {
      const value = { ...formValues, _id: uuidv4() }

      input.onChange([...(input.value ?? []), value])
      input.onBlur() // usado pra marcar o field como touched no form externo

      resolve(true)
    })

  const handleSubmitMultiple = useCallback(
    (formValues: T[], formApi: PecFormApi) => {
      const values = formValues.map((formValue) => ({ ...formValue, _id: uuidv4() }))
      input.onChange([...(input.value ?? []), ...values])
      input.onBlur() // usado pra marcar o field como touched no form externo
      resetAllFieldStates(formApi)
      setTimeout(formApi.reset)
    },
    [input]
  )
  const handleSubmitMultipleWithoutReset = useCallback(
    (formValues: T[], formApi: PecFormApi) => {
      const values = formValues.map((formValue) => ({ ...formValue, _id: uuidv4() }))
      input.onChange([...(input.value ?? []), ...values])
      input.onBlur() // usado pra marcar o field como touched no form externo
      resetAllFieldStates(formApi)
    },
    [input]
  )

  const handleRowChanged = useCallback(
    (formValues: T) => {
      input.onChange(input.value?.map((item) => (isSameRow(item, formValues) ? formValues : item)))
      input.onBlur() // usado pra marcar o field como touched no form externo
    },
    [input, isSameRow]
  )

  const removeItem = useCallback(
    (itemToRemove: T) => {
      input.onChange(input.value?.filter((item) => !isSameRow(item, itemToRemove)))
      input.onBlur() // usado pra marcar o field como touched no form externo
    },
    [input, isSameRow]
  )

  const resetItemToInitialValue = useCallback(
    (itemToReset: T) => {
      const updatedCurrentValues = getListWithRestoredItem(
        meta.initial ?? emptyArrayOf<T>(),
        input.value ?? emptyArrayOf<T>(),
        itemToReset,
        isSameRow
      )

      input.onChange(updatedCurrentValues)
      input.onBlur() // usado pra marcar o field como touched no form externo
    },
    [meta.initial, input, isSameRow]
  )

  return {
    handleSubmit,
    handleSubmitMultiple,
    handleSubmitMultipleWithoutReset,
    handleRowChanged,
    removeItem,
    resetItemToInitialValue,
    handleSubmitWithoutReset,
    ...fieldProps,
  }
}

interface EditableRowProps {
  editing: boolean
  dataTestId?: string
}

export const EditableRow = (props: PropsWithChildren<EditableRowProps>) => {
  const { children, editing, dataTestId } = props

  const theme = useTheme()

  return (
    <EditableRowWrapper editing={editing} theme={theme}>
      <div data-testid={dataTestId}>{children}</div>
    </EditableRowWrapper>
  )
}

const EditableRowWrapper = styled.div<EditableRowProps & { theme: Theme }>`
  background-color: ${(props) => props.editing && props.theme.pallete.primary.c90};
  padding: 0 1rem;
  overflow: hidden;
  transition: background-color 0.4s ease-in-out;
`

export const EditableList = (props: ComponentProps<'div'>) => {
  const theme = useTheme()
  const styles = createStyles(theme)

  return <div css={styles.editableList} {...props} />
}

const createStyles = (theme: Theme) => ({
  editableList: css`
    border: 1px solid ${theme.pallete.divider};
    border-radius: ${theme.radius.input}px;
    > div:not(:last-of-type) {
      border-bottom: 1px solid ${theme.pallete.divider};
    }
  `,
})
